import { httpClient } from '@/src/@core/lib/httpClient'
import type { FeaturePreferenceRow, FeatureVersion, ResolveFeatureResponse } from '@/src/types/featureExperience'

/** BE may wrap the payload as `{ data: { features, requestedAt } }` (Nest/global interceptor). */
function unwrapResolveResponse(body: unknown): ResolveFeatureResponse {
  if (
    body !== null &&
    typeof body === 'object' &&
    'data' in body &&
    (body as { data?: unknown }).data !== null &&
    typeof (body as { data: unknown }).data === 'object' &&
    'features' in (body as { data: ResolveFeatureResponse }).data
  ) {
    return (body as { data: ResolveFeatureResponse }).data
  }

  return body as ResolveFeatureResponse
}

function withRestaurantHeader(restaurantId: string | number | null | undefined) {
  const id = restaurantId != null && restaurantId !== '' ? String(restaurantId) : undefined

  return id ? { headers: { 'x-restaurant-id': id } as Record<string, string> } : {}
}

/**
 * GET /feature-experiences/resolve?keys=a,b
 * Keys are comma-separated per backend contract.
 */
export async function resolveFeatures(
  keys: string[],
  restaurantId?: string | number | null
): Promise<ResolveFeatureResponse> {
  const keysQuery = keys
    .map(k => k.trim())
    .filter(Boolean)
    .map(k => encodeURIComponent(k))
    .join(',')
  const { data } = await httpClient.get<ResolveFeatureResponse | { data: ResolveFeatureResponse }>(
    `/feature-experiences/resolve?keys=${keysQuery}`,
    {
      ...withRestaurantHeader(restaurantId)
    }
  )

  return unwrapResolveResponse(data)
}

export async function setPreference(
  featureKey: string,
  preferredVersion: FeatureVersion,
  restaurantId?: string | number | null
): Promise<void> {
  await httpClient.put(
    `/feature-experiences/preferences/${encodeURIComponent(featureKey)}`,
    { preferredVersion },
    withRestaurantHeader(restaurantId)
  )
}

/** Align response with OpenAPI / BE (array of rows with `userId: number`). */
export async function getPreferences(): Promise<FeaturePreferenceRow[]> {
  const { data } = await httpClient.get<FeaturePreferenceRow[]>(`/feature-experiences/preferences`)

  return data
}
